import { createApp, reactive } from 'vue'

import createEl from '@/utils/createEl';
import eventbus from '@/utils/eventbus';
import modalStack from '@/utils/modalStack';
import state from '@/state';

import Icon from '@/components/Icon.vue';

const t = state.t.prefix('modal');

var currentModal = null;

function createOverlay () {
  const overlay = createEl('div', ['gmf-modal-overlay', 'gmf-invisible', 'gmf-context'], document.body);
  overlay.setAttribute('data-gmf', '');

  return {
    element: overlay,
    destroy: () => {
      if (overlay.parentElement) {
        overlay.parentElement.removeChild(overlay);
      }
    },
    close: () => {
      overlay.classList.add('gmf-invisible');
      overlay.style['pointer-events'] = 'none';
    },
  };
}

function create (app, stack, options) {
  if (! stack) {
    eventbus.emit('modal.close');
    if (currentModal) {
      currentModal.destroy();
    }
  }

  let _app;

  const { element: overlay, destroy: overlayDestroy, close: overlayClose } = options?.overlay ? options.overlay : createOverlay();
  const modalParent = options?.modalParent ? options.modalParent : overlay;
  const modalBody = createEl('div', 'gmf-modal-body', modalParent);

  // modal accessibility
  modalBody.setAttribute('role', 'dialog');
  modalBody.setAttribute('aria-modal', 'true');

  let modal;

  function destroy () {
    modalStack.remove(modal);
    overlayDestroy();
    if (currentModal === modal) {
      currentModal = null;
    }

    if (_app) {
      _app.unmount();
    }
  }

  function close () {
    modalStack.remove(modal);
    overlayClose();
    setTimeout(() => {
      destroy();
    }, 300);
    eventbus.off('modal.close', close);
  }

  eventbus.on('modal.close', close);

  // Default overlay click handler.
  overlay.onclick = ev => {
    ev.stopPropagation();
    ev.preventDefault();

    // Check if click originates from inside the modal body.
    if (! overlay.isBodyClickEvent) {
      close();
    }

    overlay.isBodyClickEvent = false;
  };

  overlay.ontouchstart = ev => {
    ev.stopPropagation();
    overlay.isBodyClickEvent = false;
  };

  overlay.onmousedown = ev => {
    ev.stopPropagation();
    overlay.isBodyClickEvent = false;
  };

  modalBody.onclick = ev => {
    ev.stopPropagation();
    overlay.isBodyClickEvent = true;
  };

  modalBody.ontouchstart = ev => {
    ev.stopPropagation();
    overlay.isBodyClickEvent = true;
  };

  modalBody.onmousedown = ev => {
    ev.stopPropagation();
    overlay.isBodyClickEvent = true;
  };

  modalBody.ontouchmove = ev => {
    if (ev.touches.length > 1) {
      ev.preventDefault();
    }
  };

  const modalState = reactive({
    isTopModal: false,
  });

  if (app) {
    _app = app = createApp(app, options && options.props || undefined);

    if (options && options.provide) {
      for (const k in options.provide) {
        app = app.provide(k, options.provide[k]);
      }
    }

    if (options && options.unwrapInjectedRef) {
      app.config.unwrapInjectedRef = true;
    }

    if (options && options.labelledby) {
      modalBody.setAttribute('aria-labelledby', options.labelledby);
    }

    if (options && options.describedby) {
      modalBody.setAttribute('aria-describedby', options.describedby);
    }

    app.provide('modalOverlay', overlay);
    app.provide('closeModal', close);
    app.provide('modalState', modalState);

    app = app.mount(modalBody);
  }

  if (! options || ! options.disableCloseButton) {
    const modalClose = createEl('button', 'gmf-modal-close-btn', modalBody, true);
    modalClose.setAttribute('aria-label', 'Close');
    
    createApp(Icon, {
      icon: 'modal-close',
    }).mount(modalClose);

    // @TODO do we need to unmount on modal destroy?

    modalClose.onclick = close;
  }

  modal = {
    body: modalBody,
    destroy,
    app,
    modalState,
    close,
  };

  modalStack.push(modal);

  currentModal = modal;

  eventbus.emit('startPageSlideshow:stop');
  setTimeout(()=>{
    overlay.classList.remove('gmf-invisible');
  },0);

  return modal;
}

function error (message) {
  const m = create();
  const mel = createEl('div', 'gmf-err-text', m.body);
  mel.innerText = message;
  const btnWrap = createEl('div', null, m.body);
  btnWrap.style['text-align'] = 'center';
  const btn = createEl('button', ['gmf-btn', 'gmf-btn-primary'], btnWrap);
  btn.onclick = m.destroy;
  btn.innerText = t('close');
}

function confirmDelete ({
  title,
  description,
}, onYesCallback) {
  const m = create(null, true, {
    disableCloseButton: true,
  });
  m.body.classList.add('gmf-confirm-modal');
  const tel = createEl('h2', 'gmf-subheader', m.body);
  tel.innerText = title;

  if (description) {
    const del = createEl('div', 'gmf-text-small', m.body);
    del.innerText = description;
  }

  const btnWrap = createEl('div', 'gmf-confirm-modal-buttons', m.body);

  const noBtn = createEl('button', ['gmf-btn', 'gmf-btn-secondary'], btnWrap);
  noBtn.onclick = m.destroy;
  noBtn.innerText = t('no');

  const yesBtn = createEl('button', ['gmf-btn', 'gmf-btn-danger'], btnWrap);
  yesBtn.onclick = () => {
    onYesCallback();
    m.destroy();
  }
  yesBtn.innerText = t('yes_delete', true) || t('yes');
}





export default {
  confirmDelete,
  create,
  error,
  destroy () {
    if (currentModal) {
      currentModal.destroy();
    }
  },
  getCurrentModal () {
    return currentModal;
  },
  createOverlay,
};